/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';

import type {
	useSpaceIconSpaceIconQuery as useSpaceIconSpaceIconQuery$data,
	useSpaceIconSpaceIconQueryVariables as useSpaceIconSpaceIconQuery$variables,
} from './__types__/useSpaceIconSpaceIconQuery';

export const useSpaceIcon = ({
	emojiLoading,
	hasSelectedEmoji,
	contentId,
	isSpaceTypesCreationFlowEnabled,
}: {
	emojiLoading: boolean;
	hasSelectedEmoji: boolean;
	contentId: string;
	isSpaceTypesCreationFlowEnabled: boolean;
}): {
	spaceIconPath?: string;
	loading: boolean;
} => {
	const [spaceKey] = usePageSpaceKey();
	const shouldQueryForSpaceIcon =
		!emojiLoading &&
		!hasSelectedEmoji &&
		Boolean(spaceKey) &&
		isSpaceTypesCreationFlowEnabled &&
		// The feature Company Hub uses a special space type "system" which is
		// mostly hidden from the UI and the user predominantly sees the space
		// homepage / overview presented with feature-specific customizations and
		// referred to as "front cover" or "landing page". Because the space is
		// hidden from the user, the UI concent "space icon" doesn't make sense.
		!isCompanyHubSpaceKey(spaceKey);
	const { data, loading } = useQuery<
		useSpaceIconSpaceIconQuery$data,
		useSpaceIconSpaceIconQuery$variables
	>(
		gql`
			query useSpaceIconSpaceIconQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					icon {
						path
					}
					homepage {
						id
					}
				}
			}
		`,
		{
			variables: { spaceKey: spaceKey || '' },
			skip: !shouldQueryForSpaceIcon,
		},
	);
	const isEditingOverview = data?.space?.homepage?.id === contentId;
	return {
		spaceIconPath: isEditingOverview ? data?.space?.icon?.path : undefined,
		loading,
	};
};
